import { supabase, isSupabaseConfigured } from '../lib/supabase';

// ============================================
// SITE IDENTIFIER — Isolation des données
// ============================================
export const SITE_ID = 'SUCCESSLINK_AGENCIA';

export function siteKey(key: string): string {
  return `${SITE_ID}__${key}`;
}

export function isSiteKey(key: string): boolean {
  return key.startsWith(`${SITE_ID}__`);
}

export function stripSiteKey(key: string): string {
  return key.replace(`${SITE_ID}__`, '');
}

// ============================================
// TYPES
// ============================================

export interface User {
  phone: string;
  name: string;
  email: string;
  country: string;
  username: string;
  password: string;
  referralCode: string;
  referredBy?: string | null;
  accountStatus: 'pending' | 'active' | 'inactive';
  isActivated: boolean;
  activated?: boolean;
  balance: number;
  points: number;
  totalWithdrawals: number;
  level1Referrals: number;
  level2Referrals: number;
  level3Referrals: number;
  registrationDate: string;
  lastLogin?: string;
  activatedAt?: string;
  paymentMethod?: string;
  blocked?: boolean;
  whatsapp?: string;
  createdAt?: string;
}

export interface ActivationRequest {
  id: string;
  userPhone: string;
  userName: string;
  amount: number;
  currency: string;
  paymentMethod: string;
  proof: string;
  paymentPhone?: string;
  screenshotProof?: string;
  status: 'pending' | 'approved' | 'rejected';
  date: string;
  country: string;
  rejectionReason?: string;
}

export interface WithdrawalRequest {
  id: string;
  userPhone: string;
  userName: string;
  amount: number;
  currency?: string;
  paymentMethod: string;
  paymentNumber?: string;
  accountDetails?: string;
  status: 'pending' | 'validated' | 'approved' | 'rejected';
  date: string;
  type?: 'parrainage' | 'taches' | 'points';
  rejectionReason?: string;
}

// ============================================
// HELPERS LOCALSTORAGE
// ============================================

function lsGet<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function lsSet(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch { /* ignore */ }
}

// ============================================
// MAPPING Supabase → User
// ============================================

function mapSupabaseToUser(d: any): User {
  return {
    phone: d.phone,
    name: d.name || '',
    username: d.username || '',
    email: d.email || '',
    password: d.password || '',
    country: d.country || '',
    referralCode: d.referral_code || '',
    referredBy: d.referred_by || null,
    balance: Number(d.balance) || 0,
    points: Number(d.points) || 0,
    totalWithdrawals: Number(d.total_withdrawals) || 0,
    level1Referrals: Number(d.level1_referrals) || 0,
    level2Referrals: Number(d.level2_referrals) || 0,
    level3Referrals: Number(d.level3_referrals) || 0,
    isActivated: d.is_activated || false,
    accountStatus: d.account_status || 'pending',
    blocked: d.blocked || false,
    paymentMethod: d.payment_method || '',
    activatedAt: d.activated_at || undefined,
    lastLogin: d.last_login || undefined,
    registrationDate: d.created_at || new Date().toISOString(),
    createdAt: d.created_at,
  };
}

function mapUserToSupabase(user: Partial<User>): any {
  const obj: any = {};
  if (user.phone !== undefined) obj.phone = user.phone;
  if (user.name !== undefined) obj.name = user.name;
  if (user.username !== undefined) obj.username = user.username;
  if (user.email !== undefined) obj.email = user.email;
  if (user.password !== undefined) obj.password = user.password;
  if (user.country !== undefined) obj.country = user.country;
  if (user.referralCode !== undefined) obj.referral_code = user.referralCode;
  if (user.referredBy !== undefined) obj.referred_by = user.referredBy;
  if (user.balance !== undefined) obj.balance = user.balance;
  if (user.points !== undefined) obj.points = user.points;
  if (user.totalWithdrawals !== undefined) obj.total_withdrawals = user.totalWithdrawals;
  if (user.level1Referrals !== undefined) obj.level1_referrals = user.level1Referrals;
  if (user.level2Referrals !== undefined) obj.level2_referrals = user.level2Referrals;
  if (user.level3Referrals !== undefined) obj.level3_referrals = user.level3Referrals;
  if (user.isActivated !== undefined) obj.is_activated = user.isActivated;
  if (user.accountStatus !== undefined) obj.account_status = user.accountStatus;
  if (user.blocked !== undefined) obj.blocked = user.blocked;
  if (user.paymentMethod !== undefined) obj.payment_method = user.paymentMethod;
  if (user.activatedAt !== undefined) obj.activated_at = user.activatedAt;
  if (user.lastLogin !== undefined) obj.last_login = user.lastLogin;
  return obj;
}

// ============================================
// USERS — Supabase (users_v2) + localStorage cache
// ============================================

export async function saveUser(user: User): Promise<void> {
  if (isSupabaseConfigured) {
    try {
      const supabaseData = {
        ...mapUserToSupabase(user),
        site_id: SITE_ID,
        created_at: user.registrationDate || user.createdAt || new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('users_v2')
        .upsert(supabaseData, { onConflict: 'phone,site_id' });

      if (error) {
        console.error('❌ Erreur Supabase saveUser:', error);
      } else {
        console.log('✅ Utilisateur sauvegardé dans Supabase (users_v2):', user.phone);
      }
    } catch (err) {
      console.error('❌ Exception Supabase saveUser:', err);
    }
  }

  // Cache localStorage
  const key = siteKey(`user_${user.phone}`);
  localStorage.setItem(key, JSON.stringify(user));
  localStorage.setItem(`user_${user.phone}`, JSON.stringify(user));

  const allUsers = getAllUsersFromStorage();
  const filtered = allUsers.filter(u => u.phone !== user.phone);
  filtered.push(user);
  localStorage.setItem(siteKey('users'), JSON.stringify(filtered));
  localStorage.setItem('users', JSON.stringify(filtered));
}

export async function getUserByPhone(phone: string): Promise<User | null> {
  if (isSupabaseConfigured) {
    try {
      const { data, error } = await supabase
        .from('users_v2')
        .select('*')
        .eq('phone', phone)
        .eq('site_id', SITE_ID)
        .maybeSingle();

      if (error) {
        console.error('❌ Erreur Supabase getUserByPhone:', error);
      } else if (data) {
        const user = mapSupabaseToUser(data);
        localStorage.setItem(siteKey(`user_${phone}`), JSON.stringify(user));
        localStorage.setItem(`user_${phone}`, JSON.stringify(user));
        return user;
      }
    } catch (err) {
      console.error('❌ Exception Supabase getUserByPhone:', err);
    }
  }

  // Fallback localStorage
  const raw = localStorage.getItem(siteKey(`user_${phone}`)) || localStorage.getItem(`user_${phone}`);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

export async function updateUser(phone: string, updates: Partial<User>): Promise<void> {
  if (isSupabaseConfigured) {
    try {
      const supabaseUpdates = {
        ...mapUserToSupabase(updates),
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('users_v2')
        .update(supabaseUpdates)
        .eq('phone', phone)
        .eq('site_id', SITE_ID);

      if (error) {
        console.error('❌ Erreur Supabase updateUser:', error);
      } else {
        console.log('✅ Utilisateur mis à jour dans Supabase (users_v2):', phone);
      }
    } catch (err) {
      console.error('❌ Exception Supabase updateUser:', err);
    }
  }

  // Mettre à jour le cache localStorage
  const raw = localStorage.getItem(siteKey(`user_${phone}`)) || localStorage.getItem(`user_${phone}`);
  if (raw) {
    try {
      const user = JSON.parse(raw);
      const updated = { ...user, ...updates };
      localStorage.setItem(siteKey(`user_${phone}`), JSON.stringify(updated));
      localStorage.setItem(`user_${phone}`, JSON.stringify(updated));

      const allUsers = getAllUsersFromStorage();
      const filtered = allUsers.filter(u => u.phone !== phone);
      filtered.push(updated);
      localStorage.setItem(siteKey('users'), JSON.stringify(filtered));
      localStorage.setItem('users', JSON.stringify(filtered));
    } catch { /* ignore */ }
  }
}

export async function deleteUser(phone: string): Promise<void> {
  if (isSupabaseConfigured) {
    try {
      const { error } = await supabase
        .from('users_v2')
        .delete()
        .eq('phone', phone)
        .eq('site_id', SITE_ID);

      if (error) {
        console.error('❌ Erreur Supabase deleteUser:', error);
      } else {
        console.log('✅ Utilisateur supprimé de Supabase (users_v2):', phone);
      }
    } catch (err) {
      console.error('❌ Exception Supabase deleteUser:', err);
    }
  }

  localStorage.removeItem(siteKey(`user_${phone}`));
  localStorage.removeItem(`user_${phone}`);

  const allUsers = getAllUsersFromStorage();
  const filtered = allUsers.filter(u => u.phone !== phone);
  localStorage.setItem(siteKey('users'), JSON.stringify(filtered));
  localStorage.setItem('users', JSON.stringify(filtered));
}

export async function syncUsersFromRemote(): Promise<User[]> {
  if (isSupabaseConfigured) {
    try {
      const { data, error } = await supabase
        .from('users_v2')
        .select('*')
        .eq('site_id', SITE_ID)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Erreur Supabase syncUsersFromRemote:', error);
      } else if (data) {
        const users: User[] = data.map(mapSupabaseToUser);

        // Mettre en cache
        localStorage.setItem(siteKey('users'), JSON.stringify(users));
        localStorage.setItem('users', JSON.stringify(users));

        console.log(`✅ ${users.length} utilisateurs chargés depuis Supabase (users_v2)`);
        return users;
      }
    } catch (err) {
      console.error('❌ Exception Supabase syncUsersFromRemote:', err);
    }
  }

  return getAllUsersFromStorage();
}

export function getAllUsersFromStorage(): User[] {
  const raw = localStorage.getItem(siteKey('users')) || localStorage.getItem('users');
  const usersMap = new Map<string, User>();

  if (raw) {
    try {
      const parsed: User[] = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        parsed.forEach(u => { if (u && u.phone) usersMap.set(u.phone, u); });
      }
    } catch { /* ignore */ }
  }

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (
        (k.startsWith('user_') || k.startsWith(siteKey('user_'))) &&
        !k.includes('users') &&
        !k.includes('activation') &&
        !k.includes('withdrawal')
      ) {
        try {
          const val = localStorage.getItem(k);
          if (!val) continue;
          const u: User = JSON.parse(val);
          if (u && u.phone && !usersMap.has(u.phone)) {
            usersMap.set(u.phone, u);
          }
        } catch { /* ignore */ }
      }
    }
  } catch { /* ignore */ }

  const users = Array.from(usersMap.values());
  if (users.length > 0) {
    try {
      localStorage.setItem(siteKey('users'), JSON.stringify(users));
      localStorage.setItem('users', JSON.stringify(users));
    } catch { /* ignore */ }
  }

  return users;
}

// ============================================
// ACTIVATION REQUESTS — Supabase + localStorage
// ============================================

export async function saveActivationRequest(request: ActivationRequest): Promise<void> {
  if (isSupabaseConfigured) {
    try {
      const { error } = await supabase
        .from('activation_requests')
        .upsert({
          id: request.id,
          user_phone: request.userPhone,
          user_name: request.userName,
          amount: request.amount,
          currency: request.currency,
          payment_method: request.paymentMethod,
          proof: request.proof,
          payment_phone: request.paymentPhone,
          screenshot_proof: request.screenshotProof,
          status: request.status,
          rejection_reason: request.rejectionReason,
          date: request.date,
          country: request.country,
          site_id: SITE_ID,
        }, { onConflict: 'id' });

      if (error) {
        console.error('❌ Erreur Supabase saveActivationRequest:', error);
      } else {
        console.log('✅ Demande activation sauvegardée dans Supabase:', request.id);
      }
    } catch (err) {
      console.error('❌ Exception Supabase saveActivationRequest:', err);
    }
  }

  const key = siteKey('activationRequests');
  const all: ActivationRequest[] = JSON.parse(localStorage.getItem(key) || localStorage.getItem('activationRequests') || '[]');
  const filtered = all.filter(r => r.id !== request.id);
  filtered.unshift(request);
  localStorage.setItem(key, JSON.stringify(filtered));
  localStorage.setItem('activationRequests', JSON.stringify(filtered));
}

export async function getAllActivationRequests(): Promise<ActivationRequest[]> {
  if (isSupabaseConfigured) {
    try {
      const { data, error } = await supabase
        .from('activation_requests')
        .select('*')
        .eq('site_id', SITE_ID)
        .order('date', { ascending: false });

      if (error) {
        console.error('❌ Erreur Supabase getAllActivationRequests:', error);
      } else if (data) {
        const requests: ActivationRequest[] = data.map(d => ({
          id: d.id,
          userPhone: d.user_phone,
          userName: d.user_name,
          amount: d.amount,
          currency: d.currency,
          paymentMethod: d.payment_method,
          proof: d.proof,
          paymentPhone: d.payment_phone,
          screenshotProof: d.screenshot_proof,
          status: d.status,
          rejectionReason: d.rejection_reason,
          date: d.date,
          country: d.country,
        }));

        const key = siteKey('activationRequests');
        localStorage.setItem(key, JSON.stringify(requests));
        localStorage.setItem('activationRequests', JSON.stringify(requests));

        console.log(`✅ ${requests.length} demandes d'activation chargées depuis Supabase`);
        return requests;
      }
    } catch (err) {
      console.error('❌ Exception Supabase getAllActivationRequests:', err);
    }
  }

  const key = siteKey('activationRequests');
  const raw = localStorage.getItem(key) || localStorage.getItem('activationRequests');
  if (!raw) return [];
  try { return JSON.parse(raw); } catch { return []; }
}

export async function updateActivationRequestStatus(
  requestId: string,
  status: 'pending' | 'validated' | 'approved' | 'rejected',
  rejectionReason?: string
): Promise<void> {
  if (isSupabaseConfigured) {
    try {
      const updates: any = { status };
      if (rejectionReason) updates.rejection_reason = rejectionReason;

      const { error } = await supabase
        .from('activation_requests')
        .update(updates)
        .eq('id', requestId)
        .eq('site_id', SITE_ID);

      if (error) {
        console.error('❌ Erreur Supabase updateActivationRequestStatus:', error);
      } else {
        console.log('✅ Statut demande activation mis à jour:', requestId);
      }
    } catch (err) {
      console.error('❌ Exception Supabase updateActivationRequestStatus:', err);
    }
  }

  const all = await getAllActivationRequests();
  const request = all.find(r => r.id === requestId);
  if (request) {
    request.status = status as any;
    if (rejectionReason) request.rejectionReason = rejectionReason;
    const key = siteKey('activationRequests');
    localStorage.setItem(key, JSON.stringify(all));
    localStorage.setItem('activationRequests', JSON.stringify(all));
  }
}

// ============================================
// WITHDRAWAL REQUESTS — Supabase + localStorage
// ============================================

export async function saveWithdrawalRequest(request: WithdrawalRequest): Promise<void> {
  if (isSupabaseConfigured) {
    try {
      const { error } = await supabase
        .from('withdrawal_requests')
        .upsert({
          id: request.id,
          user_phone: request.userPhone,
          user_name: request.userName,
          amount: request.amount,
          currency: request.currency,
          payment_method: request.paymentMethod,
          payment_number: request.paymentNumber,
          account_details: request.accountDetails,
          status: request.status,
          rejection_reason: request.rejectionReason,
          date: request.date,
          site_id: SITE_ID,
        }, { onConflict: 'id' });

      if (error) {
        console.error('❌ Erreur Supabase saveWithdrawalRequest:', error);
      } else {
        console.log('✅ Demande retrait sauvegardée dans Supabase:', request.id);
      }
    } catch (err) {
      console.error('❌ Exception Supabase saveWithdrawalRequest:', err);
    }
  }

  const key = siteKey('withdrawalRequests');
  const all: WithdrawalRequest[] = JSON.parse(localStorage.getItem(key) || localStorage.getItem('withdrawalRequests') || '[]');
  const filtered = all.filter(r => r.id !== request.id);
  filtered.unshift(request);
  localStorage.setItem(key, JSON.stringify(filtered));
  localStorage.setItem('withdrawalRequests', JSON.stringify(filtered));
}

export async function getAllWithdrawalRequests(): Promise<WithdrawalRequest[]> {
  if (isSupabaseConfigured) {
    try {
      const { data, error } = await supabase
        .from('withdrawal_requests')
        .select('*')
        .eq('site_id', SITE_ID)
        .order('date', { ascending: false });

      if (error) {
        console.error('❌ Erreur Supabase getAllWithdrawalRequests:', error);
      } else if (data) {
        const requests: WithdrawalRequest[] = data.map(d => ({
          id: d.id,
          userPhone: d.user_phone,
          userName: d.user_name,
          amount: d.amount,
          currency: d.currency,
          paymentMethod: d.payment_method,
          paymentNumber: d.payment_number,
          accountDetails: d.account_details,
          status: d.status,
          rejectionReason: d.rejection_reason,
          date: d.date,
        }));

        const key = siteKey('withdrawalRequests');
        localStorage.setItem(key, JSON.stringify(requests));
        localStorage.setItem('withdrawalRequests', JSON.stringify(requests));

        console.log(`✅ ${requests.length} demandes de retrait chargées depuis Supabase`);
        return requests;
      }
    } catch (err) {
      console.error('❌ Exception Supabase getAllWithdrawalRequests:', err);
    }
  }

  const key = siteKey('withdrawalRequests');
  const raw = localStorage.getItem(key) || localStorage.getItem('withdrawalRequests');
  if (!raw) return [];
  try { return JSON.parse(raw); } catch { return []; }
}

export async function updateWithdrawalStatus(
  requestId: string,
  status: 'pending' | 'approved' | 'rejected',
  rejectionReason?: string
): Promise<void> {
  if (isSupabaseConfigured) {
    try {
      const updates: any = { status };
      if (rejectionReason) updates.rejection_reason = rejectionReason;

      const { error } = await supabase
        .from('withdrawal_requests')
        .update(updates)
        .eq('id', requestId)
        .eq('site_id', SITE_ID);

      if (error) {
        console.error('❌ Erreur Supabase updateWithdrawalStatus:', error);
      } else {
        console.log('✅ Statut retrait mis à jour:', requestId);
      }
    } catch (err) {
      console.error('❌ Exception Supabase updateWithdrawalStatus:', err);
    }
  }

  const all = await getAllWithdrawalRequests();
  const request = all.find(r => r.id === requestId);
  if (request) {
    request.status = status as any;
    if (rejectionReason) request.rejectionReason = rejectionReason;
    const key = siteKey('withdrawalRequests');
    localStorage.setItem(key, JSON.stringify(all));
    localStorage.setItem('withdrawalRequests', JSON.stringify(all));
  }
}

// ============================================
// PARRAINAGE
// ============================================

export async function creditReferralCommission(
  referrerIdentifier: string,
  _filleulName: string,
  level: number,
  amount: number
): Promise<void> {
  if (!referrerIdentifier) return;

  // Chercher d'abord dans Supabase
  let referrer: User | null = null;

  if (isSupabaseConfigured) {
    try {
      const identLower = referrerIdentifier.toLowerCase().trim();
      const { data } = await supabase
        .from('users_v2')
        .select('*')
        .eq('site_id', SITE_ID)
        .or(`username.ilike.${identLower},referral_code.ilike.${identLower},phone.eq.${identLower}`)
        .maybeSingle();

      if (data) referrer = mapSupabaseToUser(data);
    } catch { /* ignore */ }
  }

  // Fallback localStorage
  if (!referrer) {
    const allUsers = getAllUsersFromStorage();
    const identifierLower = referrerIdentifier.toLowerCase().trim();
    referrer = allUsers.find(u => {
      const uname = (u.username || '').toLowerCase().trim();
      const rcode = (u.referralCode || '').toLowerCase().trim();
      const phone = (u.phone || '').toLowerCase().trim();
      const name = (u.name || '').toLowerCase().trim().replace(/\s+/g, '');
      const identNorm = identifierLower.replace(/\s+/g, '');
      return (
        uname === identifierLower || uname === identNorm ||
        rcode === identifierLower || rcode === identNorm ||
        phone === identifierLower || name === identNorm
      );
    }) || null;
  }

  if (!referrer) {
    console.warn(`creditReferralCommission: parrain introuvable pour "${referrerIdentifier}"`);
    return;
  }

  const newBalance = (Number(referrer.balance) || 0) + amount;
  await updateUser(referrer.phone, { balance: newBalance });

  try {
    const ch = new BroadcastChannel('balance_update');
    ch.postMessage({ phone: referrer.phone, username: referrerIdentifier, newBalance, commission: amount, level, timestamp: Date.now() });
    ch.close();
  } catch { /* ignore */ }
}

export async function getUserByUsername(username: string): Promise<User | null> {
  if (!username) return null;

  if (isSupabaseConfigured) {
    try {
      const uLower = username.toLowerCase().trim();
      const { data } = await supabase
        .from('users_v2')
        .select('*')
        .eq('site_id', SITE_ID)
        .or(`username.ilike.${uLower},referral_code.ilike.${uLower}`)
        .maybeSingle();

      if (data) return mapSupabaseToUser(data);
    } catch { /* ignore */ }
  }

  const allUsers = getAllUsersFromStorage();
  const uLower = username.toLowerCase().trim();
  return allUsers.find(u => {
    const uname = (u.username || '').toLowerCase().trim();
    const rcode = (u.referralCode || '').toLowerCase().trim();
    const phone = (u.phone || '').toLowerCase().trim();
    return uname === uLower || rcode === uLower || phone === uLower;
  }) || null;
}

export async function getReferralsByUsername(username: string): Promise<User[]> {
  if (!username) return [];

  if (isSupabaseConfigured) {
    try {
      const { data } = await supabase
        .from('users_v2')
        .select('*')
        .eq('site_id', SITE_ID)
        .ilike('referred_by', username);

      if (data && data.length > 0) return data.map(mapSupabaseToUser);
    } catch { /* ignore */ }
  }

  const allUsers = getAllUsersFromStorage();
  const uLower = username.toLowerCase().trim();
  const uNorm = uLower.replace(/\s+/g, '');

  return allUsers.filter(u => {
    if (!u.referredBy) return false;
    const ref = String(u.referredBy).toLowerCase().trim();
    const refNorm = ref.replace(/\s+/g, '');
    return ref === uLower || refNorm === uNorm || ref === uNorm || refNorm === uLower;
  });
}

export async function updateReferralCounts(username: string): Promise<void> {
  if (!username) return;

  let referrer: User | null = null;
  if (isSupabaseConfigured) {
    try {
      const { data } = await supabase
        .from('users_v2')
        .select('*')
        .eq('site_id', SITE_ID)
        .or(`username.ilike.${username},referral_code.ilike.${username},phone.eq.${username}`)
        .maybeSingle();
      if (data) referrer = mapSupabaseToUser(data);
    } catch { /* ignore */ }
  }

  if (!referrer) {
    const allUsers = getAllUsersFromStorage();
    referrer = allUsers.find(u => u.username === username || u.referralCode === username || u.phone === username) || null;
  }

  if (!referrer) return;

  const level1 = await getReferralsByUsername(username);
  const level1Usernames = level1.map(u => u.username).filter(Boolean) as string[];

  let level2: User[] = [];
  for (const l1name of level1Usernames) {
    const l2 = await getReferralsByUsername(l1name);
    level2 = [...level2, ...l2];
  }
  const level2Usernames = level2.map(u => u.username).filter(Boolean) as string[];

  let level3: User[] = [];
  for (const l2name of level2Usernames) {
    const l3 = await getReferralsByUsername(l2name);
    level3 = [...level3, ...l3];
  }

  await updateUser(referrer.phone, {
    level1Referrals: level1.length,
    level2Referrals: level2.length,
    level3Referrals: level3.length,
  });
}

// ============================================
// COMPATIBILITY ALIASES
// ============================================

export async function saveUserToSharedDB(user: User): Promise<void> { return saveUser(user); }
export async function getUserFromSharedDB(phone: string): Promise<User | null> { return getUserByPhone(phone); }
export async function updateUserInSharedDB(phone: string, updates: Partial<User>): Promise<void> { return updateUser(phone, updates); }
export async function deleteUserFromSharedDB(phone: string): Promise<void> { return deleteUser(phone); }
export async function savePaymentRequestToSharedDB(request: ActivationRequest): Promise<void> { return saveActivationRequest(request); }
export async function getPaymentRequestsFromSharedDB(): Promise<ActivationRequest[]> { return getAllActivationRequests(); }
export async function updatePaymentRequestStatus(id: string, status: 'approved' | 'rejected', rejectionReason?: string): Promise<void> { return updateActivationRequestStatus(id, status, rejectionReason); }
export async function getAllUsersFromSharedDB(): Promise<User[]> { return syncUsersFromRemote(); }
export async function getAllPaymentRequestsFromSharedDB(): Promise<ActivationRequest[]> { return getAllActivationRequests(); }
export function invalidateCache(): void { /* rien */ }
export function migrateOldData(): void { /* rien */ }

export async function resetAllDataInSharedDB(): Promise<void> {
  const adminPhones = ['97800237', '92252664', '90804284', '92242664', '98436291', '70636321'];
  const allUsers = getAllUsersFromStorage();
  const admins = allUsers.filter(u => adminPhones.includes(u.phone));
  lsSet(siteKey('allUsers'), admins);
  lsSet(siteKey('activationRequests'), []);
  lsSet('activationRequests', []);
  lsSet(siteKey('withdrawalRequests'), []);
  lsSet('withdrawalRequests', []);
}
